import type { Anexo } from '../store/types';
import { t } from './i18n';
import { IMAGEM_DADO, urlHttp } from './linkSeguro';
import { cifrar, decifrar } from './cofre';

// Versão do navegador: imagens pequenas ficam embutidas na página; outros arquivos, use links
// (Drive, OneDrive, Dropbox). Arquivos guardados no celular só abrem no celular.

const LIMITE = 200 * 1024;
const LIMITE_NUVEM = 15 * 1024 * 1024;

export const abreAqui = (a: Anexo) => !!a.url || !!a.dado || !!a.gp;
export const temLocal = (a: Anexo) => !!a.dado;

function escolher(accept: string): Promise<globalThis.File | null> {
  return new Promise(res => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = accept;
    inp.onchange = () => res(inp.files?.[0] ?? null);
    inp.click();
  });
}

/** nuvem = vai ser guardado no Drive/OneDrive logo em seguida (aceita qualquer arquivo de até 15 MB). */
export async function escolherArquivo(imagem = false, nuvem = false): Promise<Anexo | null> {
  const f = await escolher(imagem ? 'image/*' : '*/*');
  if (!f) return null;
  if (nuvem && f.size > LIMITE_NUVEM) throw new Error(t('O arquivo passa de 15 MB.'));
  if (!nuvem && (!f.type.startsWith('image/') || f.size > LIMITE)) {
    throw new Error(t('No navegador, só dá para guardar imagens pequenas (até 200 KB). Para outros arquivos, adicione um link do Drive, OneDrive ou Dropbox, ou use o app no celular.'));
  }
  const dado = await new Promise<string>((ok, erro) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = erro; r.readAsDataURL(f); });
  return { nome: f.name, mime: f.type, tamanho: f.size, dado };
}

/** No navegador: uma imagem por vez (as fotos grandes e os vídeos vão pelo app no celular). */
export async function escolherVarios(_soImagens = false): Promise<Anexo[]> {
  const a = await escolherArquivo(true, false);
  return a ? [a] : [];
}

/** No navegador o arquivo gerado fica embutido (XML e PDF de nota fiscal são pequenos). */
export async function anexoDeBytes(nome: string, bytes: Uint8Array, mime: string): Promise<Anexo> {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return { nome, mime, tamanho: bytes.length, dado: `data:${mime};base64,${btoa(s)}` };
}
export const anexoDeTexto = (nome: string, texto: string, mime = 'application/xml') => anexoDeBytes(nome, new TextEncoder().encode(texto), mime);

export async function lerBase64(a: Anexo) {
  if (a.cofre) { const d = abertoWeb(a); return d.slice(d.indexOf(',') + 1); }
  if (!a.dado) throw new Error(t('Este arquivo foi guardado no celular. Abra pelo app no celular.'));
  return a.dado.slice(a.dado.indexOf(',') + 1);
}
export const soNesteAparelho = (a: Anexo) => !a.cofre && !a.nuvem && !a.url && !!a.dado;

/** 0.58: mostra a imagem numa aba nova criando o elemento (sem montar HTML com texto), e só se for imagem de verdade. */
function mostrarImagem(d: string) {
  if (!IMAGEM_DADO.test(d)) throw new Error(t('Este arquivo não é uma imagem válida.'));
  const w = window.open();
  if (!w) return;
  const img = w.document.createElement('img');
  img.src = d; img.style.maxWidth = '100%';
  w.document.body.appendChild(img);
}

export async function abrirAnexo(a: Anexo) {
  if (a.cofre) return abrirAnexo({ ...a, cofre: false, dado: abertoWeb(a) });
  if (a.url) { if (!urlHttp(a.url)) throw new Error(t('Link bloqueado por segurança.')); window.open(a.url, '_blank', 'noopener'); return; }
  if (a.dado && !(a.mime ?? 'image/').startsWith('image/')) {   // PDF, XML...: abre numa aba (ou baixa)
    // 0.58: o tipo é decidido aqui (PDF abre; o resto vira texto ou download), nunca uma página que rode código
    const tipo = a.mime === 'application/pdf' ? 'application/pdf' : /xml|text|json|csv/i.test(a.mime ?? '') ? 'text/plain;charset=utf-8' : 'application/octet-stream';
    const b = new Blob([await (await fetch(a.dado)).arrayBuffer()], { type: tipo });
    const u = URL.createObjectURL(b);
    if (!window.open(u, '_blank', 'noopener')) { const l = document.createElement('a'); l.href = u; l.download = a.nome; l.click(); }
    setTimeout(() => URL.revokeObjectURL(u), 60000);
    return;
  }
  if (a.dado) { mostrarImagem(a.dado); return; }
  throw new Error(t('Este arquivo foi guardado no celular. Abra pelo app no celular.'));
}

export function apagarAnexo(_: Anexo) { /* nada guardado fora da página */ }

async function dataUrlParaArquivo(a: Anexo) {
  const r = await fetch(a.dado!);
  const b = await r.blob();
  return new globalThis.File([b], a.nome, { type: a.mime || b.type });
}

/** No navegador: usa o compartilhar do sistema quando existe (celular, Windows, Mac); senão baixa ou abre. */
export async function compartilharAnexo(a: Anexo) {
  if (a.cofre) return compartilharAnexo({ ...a, cofre: false, dado: abertoWeb(a) });
  const nav: any = navigator;
  if (a.url) {
    if (nav.share) { await nav.share({ title: a.nome, url: a.url }); return; }
    await nav.clipboard?.writeText(a.url); throw new Error(t('Link copiado. Cole onde quiser enviar.'));
  }
  if (a.dado) {
    const f = await dataUrlParaArquivo(a);
    if (nav.canShare?.({ files: [f] })) { await nav.share({ files: [f], title: a.nome }); return; }
    const link = document.createElement('a'); link.href = a.dado; link.download = a.nome; link.click(); return;
  }
  throw new Error(t('Este arquivo foi guardado no celular. Abra pelo app no celular.'));
}

export async function compartilharTexto(texto: string, titulo?: string): Promise<'ok' | 'copiado'> {
  const nav: any = navigator;
  if (nav.share) { await nav.share({ title: titulo, text: texto }); return 'ok'; }
  await nav.clipboard?.writeText(texto);
  return 'copiado';
}

export async function guardarFoto(_: string): Promise<Anexo> { throw new Error('camera'); }
export async function guardarEscolhido(_: { uri: string; name?: string | null; mimeType?: string | null; size?: number | null }): Promise<Anexo> { throw new Error('galeria'); }

/** Lê um arquivo de texto escolhido (extrato OFX ou CSV). Tenta UTF-8 e, se não der, Latin-1. */
export async function escolherTexto(): Promise<{ nome: string; texto: string } | null> {
  const f = await escolher('.ofx,.csv,.txt,text/csv,application/x-ofx');
  if (!f) return null;
  const buf = await f.arrayBuffer();
  let texto = new TextDecoder('utf-8').decode(buf);
  if (texto.includes('\uFFFD')) texto = new TextDecoder('latin1').decode(buf);
  return { nome: f.name, texto };
}

// ─── Cofre (navegador): a imagem embutida fica criptografada ───
function abertoWeb(a: Anexo) {
  const d = decifrar(a.dado);
  if (d === null) throw new Error('cofre-fechado');
  if (!d) throw new Error(t('Este arquivo foi guardado no celular. Abra pelo app no celular.'));
  return d;
}
export async function protegerAnexo(a: Anexo): Promise<Anexo> {
  if (a.cofre || !a.dado) return a;
  return { nome: a.nome, mime: a.mime, tamanho: a.tamanho, dado: cifrar(a.dado), cofre: true };
}
